import {
  sendMessageEventSchema,
  ClientMessage,
  SendMessageInput,
  isV4Schema,
  Schema,
} from "@ag-kit/shared";
import {
  safeJSONParse,
  bodyToSseAsyncGenerator,
  useRefState,
  partialParse,
} from "./utils";
import {
  UIMessage,
  UIMessagePart,
  UIMessagePartToolCallWithRender,
  InterruptWithResume,
  Interrupt,
  ToolStreamingState,
} from "./type";
import * as z4 from "zod/v4/core";
import { useEffect } from "react";
import {
  clientTool,
  isClientToolWithHandler,
  isClientToolWithRender,
  isClientToolWithRenderAndWaitForResponse,
  serverTool,
} from "./tool";
import { useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { zodToJsonSchema } from "@ag-kit/zod-to-json-schema";

function toJsonSchema(schema: Schema) {
  return isV4Schema(schema) ? z4.toJSONSchema(schema) : zodToJsonSchema(schema);
}

interface UseChatOptions {
  url?: string;
  clientTools?: ReadonlyArray<ReturnType<typeof clientTool>>;
  serverTools?: ReadonlyArray<ReturnType<typeof serverTool>>;
  threadId?: string;
  interrupt?: InterruptWithResume;
  fetch?: typeof fetch;
}

export function useChat({
  clientTools,
  serverTools,
  url = "/send-message",
  threadId = uuidv4(),
  interrupt,
  fetch = globalThis.fetch,
}: UseChatOptions) {
  const threadIdRef = useRef(threadId);
  const [messages, setMessages] = useRefState<Array<ClientMessage>>([]);
  const [_, setDeltaMessages, getLatestDeltaMessages] = useRefState<
    Array<ClientMessage>
  >([]);

  const [loading] = useRefState(false);
  const [streaming, setStreaming] = useRefState(false);

  const [pendingInterrupt, setPendingInterrupt] = useRefState<Interrupt | null>(
    null
  );
  const [resolveInterrupt, setResolveInterrupt] = useRefState<
    (Interrupt & { resumePayload: unknown }) | null
  >(null);

  const [uiToolCalls, setUiToolCalls] = useRefState<
    Array<{
      id: string;
      name: string;
      arguments: string;
      result?: string;
      state: ToolStreamingState;
      errorText?: string;
    }>
  >([]);

  const [currentRoundUiToolCallIds, setCurrentRoundUiToolCallIds] = useRefState<
    Array<string>
  >([]);

  const calledToolCalls = useRef(new Set<string>());
  const [error, setError] = useRefState<null | unknown>(null);

  const uiMessages = messages.reduce<Array<UIMessage>>((acc, cur, index) => {
    if (cur.role === "user") {
      return [
        ...acc,
        {
          role: "user" as const,
          parts: [{ type: "text", text: cur.content }],
        },
      ];
    } else if (cur.role === "system") {
      return acc;
    } else {
      const lastMessage = acc.at(-1);
      if (cur.role === "assistant") {
        let parts: Array<UIMessagePart> = [];
        if (cur.content?.trim()) {
          parts.push({ type: "text", text: cur.content });
        }
        if (Array.isArray(cur.toolCalls) && cur.toolCalls.length > 0) {
          parts = [
            ...parts,
            ...cur.toolCalls.map<UIMessagePartToolCallWithRender>(
              (toolCall) => {
                const uiToolCall = uiToolCalls.find(
                  (x) => x.id === toolCall.id
                )!;

                const clientTool = clientTools?.find(
                  (tool) => tool.name === uiToolCall.name
                );

                const serverTool = serverTools?.find(
                  (tool) => tool.name === uiToolCall.name
                );

                if (clientTool) {
                  if (isClientToolWithRender(clientTool)) {
                    return {
                      ...uiToolCall,
                      type: "tool-call",
                      render: () =>
                        clientTool.render({
                          input: partialParse(uiToolCall.arguments),
                          part: { ...uiToolCall, type: "tool-call" },
                        }),
                    };
                  }
                  if (isClientToolWithRenderAndWaitForResponse(clientTool)) {
                    return {
                      ...uiToolCall,
                      type: "tool-call",
                      render: () =>
                        clientTool.renderAndWaitForResponse({
                          part: { ...uiToolCall, type: "tool-call" },
                          submitToolResult:
                            uiToolCall.state === "input-available"
                              ? (output) => {
                                  setUiToolCalls((oldToolCalls) => {
                                    const toolCallIndex =
                                      oldToolCalls.findIndex(
                                        (x) => x.id === uiToolCall.id
                                      );
                                    return [
                                      ...oldToolCalls.slice(0, toolCallIndex),
                                      {
                                        ...oldToolCalls[toolCallIndex],
                                        result: output,
                                        state: "output-available",
                                      },
                                      ...oldToolCalls.slice(toolCallIndex + 1),
                                    ];
                                  });
                                }
                              : undefined,
                        }),
                    };
                  }
                }

                if (serverTool) {
                  return {
                    ...uiToolCall,
                    type: "tool-call",
                    render: () =>
                      serverTool.render({
                        input: partialParse(uiToolCall.arguments),
                        part: { ...uiToolCall, type: "tool-call" },
                      }),
                  };
                }

                return { ...uiToolCall, type: "tool-call" };
              }
            ),
          ];
        }

        if (pendingInterrupt && !resolveInterrupt) {
          parts.push({
            type: "interrupt",
            interrupt: pendingInterrupt,
            render:
              messages
                .slice(index + 1)
                .findIndex((x) => x.role === "assistant") === -1 && interrupt
                ? () =>
                    interrupt.renderWithResume({
                      interrupt: pendingInterrupt,
                      resume: (result) => {
                        setResolveInterrupt({
                          id: pendingInterrupt.id,
                          reason: pendingInterrupt.reason,
                          payload: pendingInterrupt.payload,
                          resumePayload: result,
                        });
                      },
                    })
                : undefined,
          });
        }

        if (lastMessage === undefined) {
          return [...acc, { role: "assistant", parts }];
        }
        if (lastMessage.role === "user") {
          return [...acc, { role: "assistant", parts }];
        } else {
          return [
            ...acc.slice(0, -1),
            { ...lastMessage, parts: [...lastMessage.parts, ...parts] },
          ];
        }
      } else {
        return acc;
      }
    }
  }, []);

  const sendMessage = async (
    prop?:
      | string
      | { toolCallId: string; content: string }
      | {
          interruptId: string;
          payload: unknown;
        }
  ) => {
    setStreaming(true);
    setCurrentRoundUiToolCallIds([]);

    if (typeof prop === "string") {
      setMessages((oldMessages) => [
        ...oldMessages,
        { role: "user", content: prop },
      ]);
      setDeltaMessages((oldMessages) => [
        ...oldMessages,
        { role: "user", content: prop },
      ]);
    } else if (prop && "toolCallId" in prop) {
      setMessages((old) => [
        ...old,
        { role: "tool", content: prop.content, toolCallId: prop.toolCallId },
      ]);
      setDeltaMessages((old) => [
        ...old,
        { role: "tool", content: prop.content, toolCallId: prop.toolCallId },
      ]);
    }

    let sendMessageInput: SendMessageInput;

    if (prop && typeof prop === "object" && "interruptId" in prop) {
      sendMessageInput = {
        resume: {
          interruptId: prop.interruptId,
          payload: JSON.stringify(prop.payload),
        },
        tools: clientTools?.map((tool) => ({
          name: tool.name,
          description: tool.description,
          parameters: JSON.stringify(toJsonSchema(tool.parameters)),
        })),
        conversationId: threadIdRef.current,
      };
    } else {
      const deltaMessages = getLatestDeltaMessages();
      setDeltaMessages([]);

      sendMessageInput = {
        messages: deltaMessages,
        tools: clientTools?.map((tool) => ({
          name: tool.name,
          description: tool.description,
          parameters: JSON.stringify(toJsonSchema(tool.parameters)),
        })),
        conversationId: threadIdRef.current,
      };
    }

    const requestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
      },
      body: JSON.stringify(sendMessageInput),
    };

    try {
      const res = await fetch(url, requestInit);

      if (!res.ok) {
        throw new Error(
          JSON.stringify({
            request: {
              url,
              ...requestInit,
            },
            response: {
              status: res.status,
              statusText: res.statusText,
              body: res.bodyUsed ? null : await res.text(),
            },
          })
        );
      }

      const eventSourceGenerator = bodyToSseAsyncGenerator(res.body!);

      for await (const event of eventSourceGenerator) {
        if (event.data === "[DONE]") {
          break;
        }

        const parsed = sendMessageEventSchema.safeParse(
          safeJSONParse(event.data)
        );

        if (parsed.success !== true) {
          continue;
        }

        const { data } = parsed;

        if (data.type === "text") {
          setMessages((oldMessages) => {
            const lastMessage = oldMessages.at(-1);
            if (lastMessage?.role === "assistant") {
              return [
                ...oldMessages.slice(0, -1),
                {
                  ...lastMessage,
                  content: (lastMessage.content ?? "") + data.content,
                },
              ];
            } else {
              const newMessage: ClientMessage = {
                role: "assistant",
                content: data.content,
              };
              return [...oldMessages, newMessage];
            }
          });
        } else if (data.type === "tool-call-start") {
          const toolCall = {
            type: "function" as const,
            id: data.toolCallId,
            function: {
              arguments: "",
              name: data.toolCallName,
            },
          };
          setMessages((old) => {
            const lastMessage = old.at(-1);
            if (lastMessage?.role === "assistant") {
              return [
                ...old.slice(0, -1),
                {
                  ...lastMessage,
                  toolCalls: [...(lastMessage?.toolCalls ?? []), toolCall],
                },
              ];
            } else {
              return [...old, { role: "assistant", toolCalls: [toolCall] }];
            }
          });
          setUiToolCalls((old) => [
            ...old,
            {
              id: data.toolCallId,
              name: data.toolCallName,
              arguments: "",
              state: "input-streaming",
            },
          ]);
        } else if (data.type === "tool-call-args") {
          setMessages((old) => {
            const lastMessage = old.at(-1);
            if (lastMessage?.role !== "assistant") {
              throw new Error(`Last message is not an assistant message.`);
            }

            const toolCalls = lastMessage.toolCalls!;
            const toolCallIndex = toolCalls.findIndex(
              (x) => x.id === data.toolCallId
            );
            const nextToolCalls: typeof toolCalls = [
              ...toolCalls.slice(0, toolCallIndex),
              {
                ...toolCalls[toolCallIndex],
                function: {
                  ...toolCalls[toolCallIndex].function,
                  arguments:
                    toolCalls[toolCallIndex].function.arguments + data.delta,
                },
              },
              ...toolCalls.slice(toolCallIndex + 1),
            ];

            return [
              ...old.slice(0, -1),
              { ...lastMessage, toolCalls: nextToolCalls },
            ];
          });

          setUiToolCalls((old) => {
            const index = old.findIndex((x) => x.id === data.toolCallId)!;
            return [
              ...old.slice(0, index),
              {
                ...old[index],
                arguments: old[index].arguments + data.delta,
                state: "input-streaming",
              },
              ...old.slice(index + 1),
            ];
          });
        } else if (data.type === "tool-call-end") {
          setUiToolCalls((old) => {
            const index = old.findIndex((x) => x.id === data.toolCallId)!;
            return [
              ...old.slice(0, index),
              { ...old[index], state: "input-available" },
              ...old.slice(index + 1),
            ];
          });
          setCurrentRoundUiToolCallIds((old) => [...old, data.toolCallId]);
        } else if (data.type === "tool-result") {
          setCurrentRoundUiToolCallIds((old) =>
            old.filter((x) => x !== data.toolCallId)
          );
          setMessages((old) => [
            ...old,
            { role: "tool", content: data.result, toolCallId: data.toolCallId },
          ]);
          setUiToolCalls((old) => {
            const index = old.findIndex((x) => x.id === data.toolCallId)!;
            return [
              ...old.slice(0, index),
              { ...old[index], result: data.result, state: "output-available" },
              ...old.slice(index + 1),
            ];
          });
        } else if (data.type === "interrupt") {
          setPendingInterrupt({
            id: data.id,
            payload: data.payload,
            reason: data.reason,
          });
        }
      }

      setStreaming(false);
    } catch (e) {
      console.log(e);
      setError(e);
      setStreaming(false);
    }
  };

  const runnableTools = clientTools?.filter(isClientToolWithHandler) || [];

  const runnableToolCalls = uiToolCalls.filter(
    (toolCall) =>
      toolCall.state === "input-available" &&
      runnableTools.some((tool) => tool.name === toolCall.name)
  );

  useEffect(() => {
    if (streaming) {
      return;
    }

    runnableToolCalls
      .filter((toolCall) => !calledToolCalls.current.has(toolCall.id))
      .forEach((toolCall) => {
        calledToolCalls.current.add(toolCall.id);
        const tool = runnableTools.find((tool) => tool.name === toolCall.name)!;
        async function runToolCall() {
          return tool.handler(JSON.parse(toolCall.arguments));
        }
        Promise.resolve(runToolCall())
          .then(
            (res) => res,
            (err) =>
              `Tool call failed with info: ${typeof err === "string" ? err : String(err)}`
          )
          .then((res) => {
            setUiToolCalls((old) => {
              const index = old.findIndex((x) => x.id === toolCall.id)!;
              return [
                ...old.slice(0, index),
                {
                  ...old[index],
                  result: typeof res === "string" ? res : JSON.stringify(res),
                  state: "output-available",
                },
                ...old.slice(index + 1),
              ];
            });

            const toolResultMessage: ClientMessage = {
              role: "tool",
              content: typeof res === "string" ? res : JSON.stringify(res),
              toolCallId: toolCall.id,
            };

            setMessages((old) => [...old, toolResultMessage]);
            setDeltaMessages((old) => [...old, toolResultMessage]);
          });
      });
  }, [runnableToolCalls, runnableTools, streaming]);

  useEffect(() => {
    if (streaming) {
      return;
    }

    if (
      currentRoundUiToolCallIds.length > 0 &&
      currentRoundUiToolCallIds.every(
        (id) =>
          uiToolCalls.find((x) => x.id === id)?.state === "output-available"
      )
    ) {
      sendMessage();
    }
  }, [streaming, currentRoundUiToolCallIds, uiToolCalls]);

  useEffect(() => {
    if (
      !streaming &&
      pendingInterrupt &&
      resolveInterrupt &&
      pendingInterrupt.id === resolveInterrupt.id
    ) {
      setPendingInterrupt(null);
      setResolveInterrupt(null);
      sendMessage({
        interruptId: resolveInterrupt.id,
        payload: resolveInterrupt.resumePayload,
      });
    }
  }, [pendingInterrupt, resolveInterrupt, streaming]);

  return {
    messages,
    setMessages,
    uiMessages,
    uiToolCalls,
    sendMessage,
    loading,
    streaming,
    error,
  };
}
